import { useState, useEffect, useMemo, useCallback } from "react";
import {
  CalculateCostsResponseSchema,
  CostingMethodEnum,
  ItemComponent,
} from "@forgepoint/shared";
import {
  useComponentItemSynced,
  useItemsStore,
  useItemVersionMs,
} from "./itemsStore";
import { fetchApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Lock, Trash2 } from "lucide-react";
import Decimal from "decimal.js";
import BOMBuilder from "./BOMBuilder";
import BOMComponentSheet from "./BOMComponentSheet";
import { COSTING_METHOD_LABELS, ITEM_TYPE_LABELS } from "./itemEnumLabels";

interface BOMComponentRowProps {
  component: ItemComponent;
  quantities: string[];
  depth?: number;
  onSave?: (component: ItemComponent) => Promise<boolean>;
  onRemove?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  onUpdated?: () => void;
  parentItemId?: string;
  /** Changes after any save in this BOM tree, so a descendant's new cost rolls up here. */
  refreshKey?: number;
  /** When provided, opening the component item navigates within the parent sheet instead of opening a new one. */
  onNavigateToItem?: (itemId: string) => void;
  relatedOperationLabel?: string;
}

interface CostData {
  quantity: string;
  unitCost: string;
  unitPrice: string;
  totalCost: string;
  totalPrice: string;
}

export default function BOMComponentRow({
  component,
  quantities,
  depth = 0,
  onSave,
  onRemove,
  disabled = false,
  disabledReason,
  onUpdated,
  onNavigateToItem,
  relatedOperationLabel,
  parentItemId,
  refreshKey,
}: BOMComponentRowProps) {
  const { itemId, quantity: bomQuantity, reasoning } = component;
  const { items, fetchItem } = useItemsStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const [costs, setCosts] = useState<CostData[]>([]);
  const [isLoadingCosts, setIsLoadingCosts] = useState(false);
  const [isComponentSheetOpen, setIsComponentSheetOpen] = useState(false);

  const componentItem = items.get(itemId);
  const itemVersionMs = useItemVersionMs(itemId);
  const effectiveCostingMethod =
    component.costingMethod ?? componentItem?.costingMethod;
  const isBOM = effectiveCostingMethod === CostingMethodEnum.BOM;

  // The component item's own BOM belongs to that item, so it follows the
  // item's locks rather than the parent BOM's.
  const isComponentSynced = useComponentItemSynced(parentItemId, itemId);
  const isComponentBomLocked =
    isComponentSynced || Boolean(componentItem?.priceModelId);
  const componentLockReason = isComponentSynced
    ? "Controlled by integration"
    : "Controlled by price model";

  const effectiveQuantities = useMemo(() => {
    const scrapMultiplier = new Decimal(1).plus(
      new Decimal(component.scrapPercent ?? 0).dividedBy(100),
    );
    return quantities.map((quantity) => {
      const baseQuantity = component.fixedQuantity
        ? new Decimal(bomQuantity)
        : new Decimal(quantity).times(bomQuantity);
      return baseQuantity.times(scrapMultiplier).toString();
    });
  }, [
    quantities,
    bomQuantity,
    component.fixedQuantity,
    component.scrapPercent,
  ]);

  useEffect(() => {
    if (!componentItem) {
      fetchItem(itemId);
    }
  }, [itemId, componentItem, fetchItem]);

  useEffect(() => {
    // A superseded run must not publish: two version bumps can overlap and
    // land out of order.
    let cancelled = false;
    const fetchCosts = async () => {
      if (component.overrideComponentCost !== undefined) {
        const unitCost = component.overrideComponentCost;
        setCosts(
          effectiveQuantities.map((quantity) => {
            const totalCost = new Decimal(unitCost).times(quantity).toString();
            return {
              quantity,
              unitCost,
              unitPrice: unitCost,
              totalCost,
              totalPrice: totalCost,
            };
          }),
        );
        setIsLoadingCosts(false);
        return;
      }

      if (!itemId || !effectiveCostingMethod) {
        return;
      }

      setIsLoadingCosts(true);
      try {
        const response = await fetchApi(
          `items/${itemId}/calculate-costs`,
          "POST",
          {
            body: {
              quantities: effectiveQuantities,
              costingMethod: effectiveCostingMethod,
            },
          },
        );
        const { costs } = CalculateCostsResponseSchema.parse(response.data);

        if (!cancelled && response.ok && response.data) {
          setCosts(costs);
        }
      } catch {
        // Failed to fetch costs
      } finally {
        setIsLoadingCosts(false);
      }
    };

    fetchCosts();
    return () => {
      cancelled = true;
    };
  }, [
    itemId,
    effectiveQuantities,
    effectiveCostingMethod,
    itemVersionMs,
    component.overrideComponentCost,
    refreshKey,
  ]);

  const indentStyle =
    depth > 0 ? { paddingLeft: `${Math.min(depth * 16, 48)}px` } : {};

  const handleComponentSave = useCallback(
    async (updatedComponent: ItemComponent): Promise<boolean> => {
      if (!onSave) {
        return false;
      }
      return onSave(updatedComponent);
    },
    [onSave],
  );

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <tr
              className="group transition-colors touch-manipulation hover:bg-accent/30 cursor-pointer"
              onClick={() => setIsComponentSheetOpen(true)}
            >
              <td
                className="py-2 px-2 sm:px-4 w-[300px] min-w-[300px] sticky left-0 z-10 bg-card border-r border-border border-b border-b-border"
                data-field-anchor={`components.${component.itemId}`}
              >
                <div
                  className="flex items-center gap-1 sm:gap-2"
                  style={indentStyle}
                >
                  {isBOM && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-0 h-6 w-6 touch-manipulation shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                      }}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      )}
                    </Button>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="relative overflow-hidden">
                      <span className="text-sm font-medium whitespace-nowrap">
                        {componentItem?.name || "Loading..."}
                        {componentItem?.revision &&
                          ` Rev ${componentItem.revision}`}
                      </span>
                      {componentItem && (
                        <div className="hidden sm:absolute sm:inset-y-0 sm:right-0 sm:flex items-center gap-1 pl-6 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gradient-to-l from-card via-card to-transparent">
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 h-4 font-normal text-muted-foreground border-border bg-card"
                          >
                            {ITEM_TYPE_LABELS[componentItem.type] ??
                              componentItem.type}
                          </Badge>
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 h-4 font-normal text-muted-foreground border-border bg-card"
                          >
                            {effectiveCostingMethod
                              ? (COSTING_METHOD_LABELS[
                                  effectiveCostingMethod
                                ] ?? effectiveCostingMethod)
                              : "Unknown"}
                            {component.costingMethod ? " override" : ""}
                          </Badge>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 tabular-nums">
                      {component.fixedQuantity ? "Fixed" : "Qty"}: {bomQuantity}
                    </div>
                    {(component.fixedQuantity ||
                      component.scrapPercent ||
                      component.overrideComponentCost ||
                      component.relatedItemOperationId) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {component.fixedQuantity && (
                          <Badge variant="secondary" className="text-[10px]">
                            Per build
                          </Badge>
                        )}
                        {component.scrapPercent && (
                          <Badge variant="secondary" className="text-[10px]">
                            {component.scrapPercent}% scrap
                          </Badge>
                        )}
                        {component.overrideComponentCost && (
                          <Badge variant="secondary" className="text-[10px]">
                            Overridden
                          </Badge>
                        )}
                        {component.relatedItemOperationId && (
                          <Badge variant="secondary" className="text-[10px]">
                            {relatedOperationLabel ?? "Related operation"}
                          </Badge>
                        )}
                      </div>
                    )}
                    {reasoning && (
                      <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {reasoning}
                      </p>
                    )}
                  </div>
                  {!disabled && onRemove && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 sm:h-8 sm:w-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity touch-manipulation shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </Button>
                  )}
                </div>
              </td>
              {isLoadingCosts
                ? quantities.map((_, i) => (
                    <td
                      key={i}
                      className="py-2 px-2 sm:px-4 text-right text-xs sm:text-sm border-l border-border border-b border-b-border"
                    >
                      <span className="text-muted-foreground">...</span>
                    </td>
                  ))
                : costs.length > 0
                  ? costs.map((cost, i) => (
                      <td
                        key={i}
                        className="py-2 px-2 sm:px-4 text-right text-xs sm:text-sm border-l border-border border-b border-b-border tabular-nums"
                      >
                        <div className="flex flex-col items-end">
                          <span>
                            $
                            {new Decimal(cost.totalCost)
                              .dividedBy(
                                new Decimal(quantities[i] || 1).isZero()
                                  ? new Decimal(1)
                                  : new Decimal(quantities[i]),
                              )
                              .toNumber()
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            /bom
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            $
                            {new Decimal(cost.totalCost)
                              .toNumber()
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 3,
                              })}
                          </span>
                        </div>
                      </td>
                    ))
                  : quantities.map((_, i) => (
                      <td
                        key={i}
                        className="py-2 px-2 sm:px-4 text-right text-xs sm:text-sm border-l border-border border-b border-b-border"
                      >
                        -
                      </td>
                    ))}
            </tr>
          </TooltipTrigger>
          <TooltipContent side="left">
            {disabled ? (
              <p className="flex items-center gap-1.5">
                <Lock className="h-3 w-3" />
                {disabledReason ?? "This row is read-only"}. Click to open the
                component.
              </p>
            ) : (
              <p>Click to edit</p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {isExpanded && isBOM && componentItem && (
        <BOMBuilder
          item={componentItem}
          quantities={effectiveQuantities}
          onUpdated={onUpdated}
          depth={depth + 1}
          onNavigateToItem={onNavigateToItem}
          locked={isComponentBomLocked}
          lockReason={componentLockReason}
          refreshKey={refreshKey}
        />
      )}

      <BOMComponentSheet
        open={isComponentSheetOpen}
        onOpenChange={setIsComponentSheetOpen}
        mode="edit"
        component={component}
        onSave={handleComponentSave}
        onRemove={onRemove}
        onUpdated={onUpdated}
        parentItemId={parentItemId}
        quantities={quantities}
        locked={disabled}
        lockReason={disabledReason}
        onNavigateToItem={onNavigateToItem}
      />
    </>
  );
}
